<template>
  <div class="mx-2">
    <div class="flex flex-wrap gap-14 mb-10 justify-center">
      <div class="text-left">
        <div class="text-sm mb-2">Search company</div>
        <input
          type="search"
          placeholder="Company name..."
          class="border rounded-md p-3 outline-blue-400 w-64"
          v-model="companySearch"
        />
      </div>

      <div class="text-left">
        <div class="text-sm mb-2">Search document</div>
        <input
          type="search"
          placeholder="Document name..."
          class="border rounded-md p-3 outline-blue-400 w-64"
          v-model="documentSearch"
        />
      </div>
    </div>

    <div v-if="companySearch.length || documentSearch.length" class="mb-4">
      <span v-if="filteredCompanyData.length">
        <span v-if="companySearch.length">
          Search results for the company "{{ companySearch }}"
        </span>
        <span v-if="companySearch.length && documentSearch.length">
          containing
        </span>
        <span v-if="!companySearch.length && documentSearch.length">
          Search results for
        </span>
        <span>the document "{{ documentSearch }}"</span>
      </span>
      <span v-else>
        <span v-if="companySearch.length">
          No results for the company "{{ companySearch }}"
        </span>
        <span v-if="companySearch.length && documentSearch.length">
          containing
        </span>
        <span v-if="!companySearch.length && documentSearch.length">
          No results for
        </span>
        <span>the document "{{ documentSearch }}"</span>
      </span>
    </div>

    <Loading v-if="isFetching" />

    <ListBody
      :companyData="filteredCompanyData"
      :companySearchKeyword="companySearch"
      :documentSearchKeyword="documentSearch"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed, getCurrentInstance } from "vue";
import axios from "axios";
import Loading from "./components/Loading.vue";
import ListBody from "./components/ListBody.vue";
import { splitData, combineData } from "./helpers/splitData";

export default {
  name: "App",
  components: {
    Loading,
    ListBody,
  },
  setup() {
    const companyData = ref([]);
    const isFetching = ref(false);
    const companySearch = ref("");
    const documentSearch = ref("");

    const filteredCompanyData = computed(() => {
      const data = companyData.value.filter((c) => !!c);
      if (!companySearch.value?.length && !documentSearch.value?.length) {
        return data;
      }

      let filtered = [...data];
      const specialChars = /[^A-Z0-9]/gi;
      const documentConditions = documentSearch.value
        .toLowerCase()
        .split(specialChars);

      //filter by company
      if (companySearch.value.toLowerCase()) {
        filtered = filtered.filter((company) =>
          company.Name.toLowerCase().includes(companySearch.value.toLowerCase())
        );
      }

      //search for document
      if (documentSearch.value?.length) {
        filtered = filtered.filter((company) =>
          company.Folders.find((folder) =>
            documentConditions.every((keyword) =>
              folder.Name.toLowerCase()
                .replace(specialChars, "")
                .includes(keyword)
            )
          )
        );
      }

      return filtered;
    });

    onMounted(async () => {
      // check if localStorage is available to use
      let companyList = {};
      const companyDatabase = [];

      let chunkIndex = 0;
      let chunk;
      do {
        chunk = localStorage?.getItem("cring-company-database-" + chunkIndex);
        if (chunk) {
          companyDatabase.push(chunk);
        }
        chunkIndex += 1;
      } while (!!chunk);

      if (!companyDatabase.length) {
        companyList = await getDocumentData();
      } else {
        companyList = JSON.parse(combineData(companyDatabase));
        companyList.Companies = companyList.Companies.filter(
          (company) => !!company
        );
      }

      companyData.value = companyList.Companies;
    });

    const getDocumentData = async () => {
      const app = getCurrentInstance();

      isFetching.value = true;
      const res = await axios.get(
        "https://app.sectorcatalog.be/SectorCatalogBE/feed/v2/digestedcatalogItems?format=json&SecureGuid=d34372f9-2cdb-4944-8ec4-cf595632cc7b"
      );

      // Filter companies
      const companyCodes =
        app.appContext.config.globalProperties.$documentOrder;

      res.data.Companies = companyCodes
        .map((code) =>
          res.data.Companies.find((company) => company.Code === code.toString())
        )
        .filter((company) => !!company);

      const pluginData = window.cringDocumentData || [];

      res.data.Companies = [...res.data.Companies, ...pluginData];

      isFetching.value = false;
      if (typeof localStorage !== "undefined") {
        try {
          const dataChunks = splitData(JSON.stringify(res.data));

          dataChunks.forEach((chunk, index) => {
            localStorage.setItem("cring-company-database-" + index, chunk);
          });
        } catch (e) {
          console.log(e);
        }
      }

      return res.data;
    };

    return {
      filteredCompanyData,
      companySearch,
      documentSearch,
      isFetching,
    };
  },
};
</script>

<style ListBody>
@import url("https://fonts.googleapis.com/css2?family=Mulish:ital,wght@0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap");

#app {
  font-family: "Mulish", serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  margin-top: 60px;
}
</style>
